import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import Layout from "../component/layout";
import { toastify } from "../../../utils/toast";
import { makeRequest2 } from "../../../utils/makeRequest";
import { powerMeterBaseUrl, getFacilitiesURL, get_units_per_facility } from "../../../utils/urls";

const AddPowerMeter = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [meterSerialNumber, setMeterSerialNumber] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [selectedGatewayId, setSelectedGatewayId] = useState(""); 
  const [meterReading, setMeterReading] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [type, setType] = useState("");
  const [meterStatus, setMeterStatus] = useState("installed"); 
  const [facilityId, setFacilityId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [accountNumber, setAccountNumber] = useState("");

  // Config states
  const [manufacturers, setManufacturers] = useState([]);
  const [meterTypes, setMeterTypes] = useState([]);
  const [meterStatusOptions, setMeterStatusOptions] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [facilityUnits, setFacilityUnits] = useState([]);
  const [gateways, setGateways] = useState([]); 

  // Base URLs for configs
  const manufacturerBaseUrl = '/api/core/power_meter_management/controllers/manufacturers';
  const powerMeterUrl = '/api/core/power_meter_management/controllers/power_meters';
  const gatewayBaseUrl = '/api/core/power_meter_management/controllers/gateways';
  const facilitiesBaseUrl = getFacilitiesURL;

  // Fetch all configuration data on component mount
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        setIsLoading(true);
        
        // Fetch manufacturers
        try {
          const manufacturersRes = await makeRequest2(`${manufacturerBaseUrl}/get_all`, 'GET');
          console.log('Manufacturers response:', manufacturersRes);
          
          if (manufacturersRes && (manufacturersRes.success || manufacturersRes.data.data)) {
            const manufacturersData = manufacturersRes.data.data || [];
            
            if (Array.isArray(manufacturersData)) {
              setManufacturers(manufacturersData);
            } else {
              console.warn('Manufacturers data is not an array:', manufacturersData);
              setManufacturers([]);
            }
          } else {
            console.log('Failed to fetch manufacturers, using fallback');
            setManufacturers([]);
          }
        } catch (manufacturersError) {
          console.error('Error fetching manufacturers:', manufacturersError);
          setManufacturers([]);
        }

        // Set meter types (aligned with schema enum)
        setMeterTypes([
          { _id: '2-phase', name: '2 phase' },
          { _id: '3-phase', name: '3 phase' }
        ]);

        // Set meter status options (aligned with schema enum)
        setMeterStatusOptions([
          { _id: 'installed', name: 'Installed' },
          { _id: 'not installed', name: 'Not Installed' }
        ]);

        // Fetch facilities
        try {
          const facilitiesRes = await makeRequest2(facilitiesBaseUrl, 'GET');
          console.log('Facilities response:', facilitiesRes);
          
          if (facilitiesRes && facilitiesRes.success) {
            let facilitiesData = [];
            
            if (Array.isArray(facilitiesRes.data)) {
              facilitiesData = facilitiesRes.data;
            } else if (facilitiesRes.data && Array.isArray(facilitiesRes.data.facilities)) {
              facilitiesData = facilitiesRes.data.facilities;
            } else if (facilitiesRes.facilities && Array.isArray(facilitiesRes.facilities)) {
              facilitiesData = facilitiesRes.facilities;
            }
            
            setFacilities(facilitiesData);
          } else {
            setFacilities([]);
          }
        } catch (facilitiesError) {
          console.error('Error fetching facilities:', facilitiesError);
          setFacilities([]);
        }

        // Fetch gateways
        try {
          const gatewaysRes = await makeRequest2(`${gatewayBaseUrl}/get_all_gateways`, 'GET');
          console.log('Gateways response:', gatewaysRes);
          
          if (gatewaysRes && gatewaysRes.data.data) {
            setGateways(gatewaysRes.data.data);
          } else {
            console.log('Failed to fetch gateways');
            setGateways([]);
          }
        } catch (gatewaysError) {
          console.error('Error fetching gateways:', gatewaysError);
          setGateways([]);
        }
        
      } catch (error) {
        console.error('Error in fetchConfigs:', error);
        toastify('Error loading configuration data', 'error');
        
        // Set fallback empty arrays to prevent map errors
        setManufacturers([]);
        setFacilities([]);
        setGateways([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfigs();
  }, []);

  // Fetch units for a specific facility
  const fetchFacilityUnits = async (facilityId) => {
    if (!facilityId) {
      setFacilityUnits([]);
      setUnitId("");
      return;
    }

    try {
      setIsLoading(true);
      const response = await makeRequest2(`${get_units_per_facility}/${facilityId}`, 'GET');

      if (response.success) {
        setFacilityUnits(response.data || []);
      } else {
        console.warn('Failed to fetch units');
        setFacilityUnits([]);
      }
    } catch (error) {
      console.error('Error fetching units:', error);
      toastify('Error loading units for this facility', 'error');
      setFacilityUnits([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle facility selection change
  const handleFacilityChange = (e) => {
    const selectedFacilityId = e.target.value;
    setFacilityId(selectedFacilityId);
    setUnitId(""); 
    fetchFacilityUnits(selectedFacilityId);
  };

  // Filter gateways by selected facility
  const filteredGateways = gateways.filter(gateway => 
    !facilityId || gateway.facilityId === facilityId
  );

  const handleSubmit = async () => {
    // Basic field validations
    if (!meterSerialNumber.trim()) {
      toastify("Meter Serial Number is required", "error");
      return;
    }
    if (!selectedGatewayId) {
      toastify("Gateway is required", "error");
      return;
    }
    if (!manufacturer) {
      toastify("Manufacturer is required", "error");
      return;
    }
    if (!type) {
      toastify("Meter Type is required", "error");
      return;
    }
    if (!facilityId) {
      toastify("Facility is required", "error");
      return;
    }

    setIsSubmitting(true);

    // Find the selected gateway to get its details
    const selectedGateway = gateways.find(g => g._id === selectedGatewayId);

    const powerMeterData = {
      meterSerialNumber: meterSerialNumber.trim(),
      gatewayId: selectedGateway?.gatewayId || null, 
      previousMeterReading: parseFloat(meterReading) || 0,
      manufacturer,
      type,
      meterStatus,
      facilityId,
      lastUpdated: new Date().toISOString(),
    };

    // Add optional fields if they have values
    if (deviceId) {
      powerMeterData.deviceId = parseInt(deviceId);
    }

    // Add unit if selected
    if (unitId) {
      powerMeterData.unitId = unitId;
    }

    // Add account number if provided
    if (accountNumber) {
      powerMeterData.accountNumber = parseInt(accountNumber);
    }

    try {
      const response = await makeRequest2(
        `${powerMeterUrl}/add_meter`,
        "POST",
        powerMeterData
      );

      if (response.success || response.message) {
        toastify("Power meter added successfully", "success");
        handleClear();
        navigate("/core/power_meter_management/power_meters_management");
      } else {
        toastify(response.error || "Failed to add power meter", "error");
      }
    } catch (error) {
      console.error("Error adding power meter:", error);
      toastify("An unexpected error occurred while adding the power meter", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setMeterSerialNumber("");
    setDeviceId("");
    setSelectedGatewayId("");
    setMeterReading("");
    setManufacturer("");
    setType("");
    setMeterStatus("installed");
    setFacilityId("");
    setUnitId("");
    setAccountNumber("");
    setFacilityUnits([]);
  };

  const getFacilityName = (facilityId) => {
    const facility = facilities.find(f => f._id === facilityId);
    return facility ? facility.name : 'Unknown Facility';
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="text-center p-5">
          Loading configuration data...
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/core/power_meter_management/power_meters">Power Meters</Link>
                </li>
                <li className="breadcrumb-item">Add New Power Meter</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <Link to="#" onClick={() => navigate(-1)}>
                <i className="ti ti-arrow-narrow-left"></i> Back
              </Link>
            </div>
          </div>
          <div className="card">
            <div className="card-header">
              <h5>Add New Power Meter</h5>
            </div>
            <div className="card-body">
              <div className="container">
                <div className="row">
                  {/* Facility Dropdown - Required */}
                  <div className="col-sm-6">
                    <label className="form-label">
                      Facility<span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      className="form-control"
                      value={facilityId}
                      onChange={handleFacilityChange}
                      disabled={isSubmitting}
                    >
                      <option value="">Select Facility</option>
                      {facilities.map((facility) => (
                        <option key={facility._id} value={facility._id}>
                          {facility.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Gateway Dropdown - Required */}
                  <div className="col-sm-6">
                    <label className="form-label">
                      Gateway<span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      className="form-control"
                      value={selectedGatewayId}
                      onChange={(e) => setSelectedGatewayId(e.target.value)}
                      disabled={isSubmitting || !facilityId}
                    >
                      <option value="">Select Gateway</option>
                      {filteredGateways.map((gateway) => (
                        <option key={gateway._id} value={gateway._id}>
                          {gateway.gatewayName} - {getFacilityName(gateway.facilityId)} 
                          {gateway.gatewayId && ` (ID: ${gateway.gatewayId})`}
                        </option>
                      ))}
                    </select>
                    {facilityId && filteredGateways.length === 0 && !isLoading && (
                      <small className="text-muted">No gateways found for this facility</small>
                    )}
                    {!facilityId && (
                      <small className="text-muted">
                        Please select a facility first to load available gateways
                      </small>
                    )}
                  </div>

                  {/* Unit Dropdown - Optional */}
                  <div className="col-sm-6">
                    <label className="form-label">Unit (Optional)</label>
                    <select
                      className="form-control"
                      value={unitId}
                      onChange={(e) => setUnitId(e.target.value)}
                      disabled={isSubmitting || facilityUnits.length === 0}
                    >
                      <option value="">Select Unit</option>
                      {facilityUnits.map((unit) => (
                        <option key={unit._id} value={unit._id}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                    {facilityId && facilityUnits.length === 0 && !isLoading && (
                      <small className="text-muted">No units found for this facility</small>
                    )}
                    {!facilityId && (
                      <small className="text-muted">
                        Please select a facility first to load available units
                      </small>
                    )}
                  </div>

                  {/* Meter Serial Number */}
                  <div className="col-sm-6">
                    <label className="form-label">
                      Meter Serial Number<span style={{ color: "red" }}>*</span>
                    </label>
                    <InputText
                      className="form-control"
                      value={meterSerialNumber}
                      onChange={(e) => setMeterSerialNumber(e.target.value)}
                      placeholder="Enter meter serial number"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Account Number - Optional */}
                  <div className="col-sm-6">
                    <label className="form-label">
                      Account Number (Optional)
                    </label>
                    <InputText
                      className="form-control"
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                      placeholder="Enter account number"
                      type="number"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Device ID */}
                  <div className="col-sm-6">
                    <label className="form-label">
                      Device ID (Optional)
                    </label>
                    <InputText
                      className="form-control"
                      value={deviceId}
                      onChange={(e) => setDeviceId(e.target.value)}
                      placeholder="Enter device ID"
                      type="number"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Initial Meter Reading */}
                  <div className="col-sm-6">
                    <label className="form-label">
                      Initial Meter Reading (kWh)<span style={{ color: "red" }}>*</span>
                    </label>
                    <InputText
                      className="form-control"
                      value={meterReading}
                      onChange={(e) => setMeterReading(e.target.value)}
                      placeholder="Enter initial reading"
                      type="number"
                      min="0"
                      step="0.01"
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Manufacturer */}
                  <div className="col-sm-6">
                    <label className="form-label">
                      Manufacturer<span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      className="form-control"
                      value={manufacturer}
                      onChange={(e) => setManufacturer(e.target.value)}
                      disabled={isSubmitting}
                    >
                      <option value="">Select Manufacturer</option>
                      {manufacturers.map((mfr) => (
                        <option key={mfr._id} value={mfr.name}>
                          {mfr.name}
                        </option>
                      ))}
                    </select>
                    {manufacturers.length === 0 && (
                      <small className="text-muted">
                        No manufacturers found. Please add manufacturers in the configuration section.
                      </small>
                    )}
                  </div>

                  {/* Meter Type */}
                  <div className="col-sm-6">
                    <label className="form-label">
                      Meter Type<span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      className="form-control"
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      disabled={isSubmitting}
                    >
                      <option value="">Select Type</option>
                      {meterTypes.map((t) => (
                        <option key={t._id} value={t.name}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Meter Status - Optional with default */}
                  <div className="col-sm-6">
                    <label className="form-label">Meter Status</label>
                    <select
                      className="form-control"
                      value={meterStatus}
                      onChange={(e) => setMeterStatus(e.target.value)}
                      disabled={isSubmitting}
                    >
                      {meterStatusOptions.map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="text-end mt-4">
                <Button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleClear}
                  disabled={isSubmitting}
                >
                  Clear
                </Button>
                &nbsp;
                <Button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AddPowerMeter;